
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Calendar, Users, Clock, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useWeeklyReportData } from '@/hooks/useWeeklyReportData';
import { getMondayISO } from '@/store/weekStore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const getWeekBounds = (date: Date) => {
  const mondayISO = getMondayISO(date);
  const start = new Date(mondayISO + 'T00:00:00');
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const toInputDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const WeeklyReport: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(() => getWeekBounds(new Date()));
  const { reportData, loading } = useWeeklyReportData(dateRange.start, dateRange.end);

  const goWeek = (offset: number) => {
    const d = new Date(dateRange.start);
    d.setDate(d.getDate() + offset * 7);
    setDateRange(getWeekBounds(d));
  };

  const handleCustomStart = (val: string) => {
    const d = new Date(val + 'T00:00:00');
    if (!isNaN(d.getTime())) setDateRange(prev => ({ ...prev, start: d }));
  };

  const handleCustomEnd = (val: string) => {
    const d = new Date(val + 'T23:59:59');
    if (!isNaN(d.getTime())) setDateRange(prev => ({ ...prev, end: d }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-8">
            <div className="text-lg">Chargement du rapport...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate('/manager')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Rapport Hebdomadaire</h1>
              <p className="text-muted-foreground">
                {reportData.weekStart} au {reportData.weekEnd}
              </p>
            </div>
          </div>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Télécharger PDF
          </Button>
        </div>

        {/* Date Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => goWeek(-1)}>← Semaine préc.</Button>
              <Button variant="outline" size="sm" onClick={() => setDateRange(getWeekBounds(new Date()))}>Cette semaine</Button>
              <Button variant="outline" size="sm" onClick={() => goWeek(1)}>Semaine suiv. →</Button>
              <div className="flex items-center gap-2 ml-auto">
                <label className="text-sm text-muted-foreground">Du</label>
                <input type="date" className="border rounded px-2 py-1 text-sm" value={toInputDate(dateRange.start)} onChange={e => handleCustomStart(e.target.value)} />
                <label className="text-sm text-muted-foreground">au</label>
                <input type="date" className="border rounded px-2 py-1 text-sm" value={toInputDate(dateRange.end)} onChange={e => handleCustomEnd(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <div className="text-2xl font-bold">{reportData.stats.activeEmployees}</div>
              <div className="text-sm text-muted-foreground">Employés actifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <div className="text-2xl font-bold">{reportData.stats.totalHours}h</div>
              <div className="text-sm text-muted-foreground">Heures totales</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-8 w-8 mx-auto mb-2 text-orange-600" />
              <div className="text-2xl font-bold">{reportData.stats.productivity}%</div>
              <div className="text-sm text-muted-foreground">Productivité</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-purple-600" />
              <div className="text-2xl font-bold">{reportData.stats.avgHoursPerDay}h</div>
              <div className="text-sm text-muted-foreground">Moyenne par jour</div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle>Heures par jour</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.dailyHours}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hours" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Performance par employé</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.employeePerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hours" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Project Distribution */}
        <Card>
          <CardHeader><CardTitle>Répartition par projet</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reportData.projectDistribution.map((project, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{project.name}</span>
                      <span>{project.hours}h ({project.percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${project.percentage}%` }} />
                    </div>
                  </div>
                </div>
              ))}
              {reportData.projectDistribution.length === 0 && (
                <p className="text-muted-foreground text-center py-4">Aucune donnée pour cette période</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card>
          <CardHeader><CardTitle>Résumé</CardTitle></CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              <p className="text-muted-foreground">
                Sur cette période, l'équipe a enregistré un total de {reportData.stats.totalHours} heures
                avec une moyenne de {reportData.stats.avgHoursPerDay} heures par jour.
                {reportData.stats.activeEmployees > 0 && ` ${reportData.stats.activeEmployees} employé(s) actif(s).`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
